import { BdGraphNodeStateMap, BdGraphStateMap, BdGraph, BdGraphNode } from './graph.model';
import { GraphUtils } from './graph-utils';
import { PropertyVotesModel } from './models/property-votes.model';
import { VotesModel } from './models/votes.model';
import { ElectionResultModel } from './models/election-result.model';

export namespace Elections {
  export interface SyncRules {
    [property: string]: 'majorityWins' | 'trueWins' | 'falseWins';
  }

  export function createVoter(properties: string[], state: BdGraphNodeStateMap): Object {
    return properties.reduce((acc, property) => {
      acc[property] = state[property] || false;
      return acc;
    }, {});
  }

  export function getInitialVoterState(
    leafStateMap: BdGraphStateMap,
    properties: string[],
  ): BdGraphStateMap {
    const nodeIds = Object.keys(leafStateMap);
    return nodeIds.reduce((voters, nodeId) => {
      voters[nodeId] = createVoter(properties, leafStateMap[nodeId]);
      return voters;
    }, {});
  }

  export function getInitialElectionDepth(
    leafStateMap: BdGraphStateMap,
    graph: BdGraph<BdGraphNode>,
  ) {
    const nodeIds = Object.keys(leafStateMap);
    const startDepth = 0;

    const depth = nodeIds.reduce((_depth, nodeId) => {
      return Math.max(_depth, GraphUtils.getNode(graph, nodeId).depth);
    }, startDepth);

    return depth - 1;
  }

  // [voting] get all ancestor nodes at the next depth
  export function getEligibleElections(
    graph: BdGraph<BdGraphNode>,
    voterIds: string[],
    depth: number,
  ) {
    const ancestorNodeIds = GraphUtils.getAllAncestorsFromList(graph, voterIds);
    return ancestorNodeIds.filter((nodeId) => {
      return nodeId && GraphUtils.getNode(graph, nodeId).depth === depth;
    });
  }

  // capture if a vote was cast for or against a property
  function buildPropertyVotes(propSyncMap, voters: BdGraphStateMap[]) {
    const properties = Object.keys(propSyncMap);

    return properties.reduce((acc, property) => {
      const votes = voters.reduce<{ votesFor: string[]; votesAgainst: string[] }>(
        (_votes, voter) => {
          const voterId = Object.keys(voter)[0];

          if (voter[voterId][property]) {
            _votes.votesFor.push(voterId);
          } else {
            _votes.votesAgainst.push(voterId);
          }
          return _votes;
        },
        { votesFor: [], votesAgainst: [] },
      );

      acc[property] = votes;
      return acc;
    }, {});
  }

  const trueWinsRules = (tallies: VotesModel) => {
    return { result: tallies.votesFor.length > 0, dissenters: [...tallies.votesAgainst] };
  };

  const falseWinsRules = (tallies: VotesModel) => {
    return { result: tallies.votesAgainst.length > 0, dissenters: [...tallies.votesFor] };
  };

  const majorityWinsRules = (tallies: VotesModel) => {
    const result = tallies.votesFor.length > tallies.votesAgainst.length;
    const dissenters = result ? [...tallies.votesAgainst] : [...tallies.votesFor];
    return { result, dissenters };
  };

  const ruleFns = {
    trueWins: trueWinsRules,
    falseWins: falseWinsRules,
    majorityWins: majorityWinsRules,
  };

  export function buildElectionResult(
    electionId: string,
    voteTallies: PropertyVotesModel,
    propSyncMap: Object,
  ): ElectionResultModel {
    const properties = Object.keys(voteTallies);

    return properties.reduce((acc, property) => {
      const tallies = voteTallies[property];
      const rule = propSyncMap[property] || 'majorityWins'; // safety net
      const { result, dissenters } = ruleFns[rule](tallies);

      acc[property] = {
        id: electionId,
        result,
        dissenters,
        voters: [...tallies.votesFor, ...tallies.votesAgainst],
      };

      return acc;
    }, {});
  }

  export function getElectionResults<TNode extends BdGraphNode>(
    graph: BdGraph<TNode>,
    voters: BdGraphStateMap,
    elections: string[],
    propSyncMap: SyncRules,
  ): { results: ElectionResultModel[]; remainingVoters: BdGraphStateMap } {
    const remainingVoters = voters;
    const properties = Object.keys(propSyncMap);

    const results = elections.reduce(
      (acc, electionId) => {
        const successors = GraphUtils.getSuccessors(graph, electionId);
        const successorVoters = successors.map((id) => {
          return {
            [id]: voters[id] || createVoter(properties, GraphUtils.getState(graph, id, properties)),
          };
        });

        successors.forEach((successorId) => {
          delete remainingVoters[successorId];
        });

        const electionTallies = buildPropertyVotes(propSyncMap, successorVoters);
        const electionResults = buildElectionResult(electionId, electionTallies, propSyncMap);

        remainingVoters[electionId] = Object.entries(electionResults).reduce(
          (_acc, [property, result]) => {
            _acc[property] = result.result;
            return _acc;
          },
          {},
        );

        acc.results.push(electionResults);

        return acc;
      },
      { results: [] },
    );

    return {
      results: results.results,
      remainingVoters,
    };
  }
}
